/* GCS Startseite (Baustein 3) – geladen von gcs-hero (nur ab zwei Slides) und gcs-team (defer). Kundenstimmen: gcs-reviews.js.
   Mehrfach eingebunden = mehrfach ausgeführt, darum ist jedes Custom Element gegen doppeltes Registrieren geschützt.
   <gcs-hero-slider>: Hero-Slider mit Crossfade, Autoplay, Strich-Indikatoren mit Fortschritt, Pause/Play, Wischen.
   <gcs-carousel>:    Scroll-Snap-Track mit Buttons vor/zurück (ab Desktop sichtbar), Zustand über passive Scroll-Events. */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Hero-Slider. Zeitgeber ist die CSS-Animation des aktiven Fortschrittsstrichs (animationend = weiter):
     pausieren heißt animation-play-state: paused, Fortschritt und Zeit bleiben so immer synchron.
     data-playing = Autoplay aktiv (Setting an, nicht per Knopf angehalten, keine reduzierte Bewegung).
     data-running = zusätzlich kein Haltegrund (Maus, Tastaturfokus, Berührung, Tab unsichtbar, außerhalb, Editor). */
  if (!customElements.get('gcs-hero-slider')) {
    const LABEL_PAUSE = 'Automatischen Wechsel anhalten';
    const LABEL_PLAY = 'Automatischen Wechsel fortsetzen';
    const SWIPE_MIN = 40;

    class GcsHeroSlider extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.slides = [...this.querySelectorAll('[data-gcs-slide]')];
        this.dots = [...this.querySelectorAll('[data-gcs-hero-dot]')];
        this.track = this.querySelector('[data-gcs-hero-track]');
        this.controls = this.querySelector('[data-gcs-hero-controls]');
        this.toggleBtn = this.querySelector('[data-gcs-hero-toggle]');
        if (this.slides.length < 2 || !this.track || !this.controls) return;
        this.initialized = true;
        this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
        this.autoplay = this.hasAttribute('data-autoplay');
        this.userPaused = false;
        this.holds = new Set();
        this.pending = false;
        this.token = 0;
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onMotion = () => this.sync();
        this.onVisibility = () => this.setHold('hidden', document.hidden);
        this.onAnimationEnd = (e) => {
          if (e.animationName !== 'gcs-hero-progress') return;
          if (e.target.closest('[data-gcs-hero-dot]') !== this.dots[this.index]) return;
          this.advance();
        };
        this.onEditorSelect = (e) => {
          const i = this.slides.findIndex((s) => s.contains(e.target));
          if (i < 0) return;
          this.go(i);
          this.setHold('editor', true);
        };
        this.onEditorDeselect = (e) => {
          if (this.contains(e.target)) this.setHold('editor', false);
        };

        this.controls.hidden = false;
        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => this.go(i));
          dot.addEventListener('keydown', (e) => this.onDotKey(e, i));
        });
        if (this.toggleBtn) {
          this.toggleBtn.addEventListener('click', () => {
            this.userPaused = !this.userPaused;
            this.sync();
          });
        }

        // Vorübergehend anhalten: Maus darüber (nur echte Maus), Tastaturfokus im Slider
        this.addEventListener('pointerenter', (e) => {
          if (e.pointerType === 'mouse') this.setHold('hover', true);
        });
        this.addEventListener('pointerleave', (e) => {
          if (e.pointerType === 'mouse') this.setHold('hover', false);
        });
        this.addEventListener('focusin', (e) => {
          if (e.target === this.toggleBtn) return;
          let visible = true;
          try {
            visible = e.target.matches(':focus-visible');
          } catch (err) {
            /* ältere Browser: jeder Fokus hält an */
          }
          if (visible) this.setHold('focus', true);
        });
        this.addEventListener('focusout', (e) => {
          if (!this.contains(e.relatedTarget)) this.setHold('focus', false);
        });

        // Wischen (Touch/Stift). touch-action: pan-y lässt senkrechtes Scrollen beim Browser.
        this.track.addEventListener('pointerdown', (e) => this.onDown(e), { passive: true });
        this.track.addEventListener('pointermove', (e) => this.onMove(e), { passive: true });
        this.track.addEventListener('pointerup', (e) => this.onUp(e, true), { passive: true });
        this.track.addEventListener('pointercancel', (e) => this.onUp(e, false), { passive: true });

        this.addEventListener('animationend', this.onAnimationEnd);
        document.addEventListener('visibilitychange', this.onVisibility);
        if (this.motion.addEventListener) this.motion.addEventListener('change', this.onMotion);
        document.addEventListener('shopify:block:select', this.onEditorSelect);
        document.addEventListener('shopify:block:deselect', this.onEditorDeselect);

        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entries) => {
              const entry = entries[entries.length - 1];
              this.setHold('offscreen', !entry.isIntersecting || entry.intersectionRatio < 0.35);
            },
            { threshold: [0, 0.35, 0.6] }
          );
          this.io.observe(this);
        }
        if (document.hidden) this.holds.add('hidden');

        // Weitere Slides erst nach dem load-Event vorladen (LCP bleibt allein bei Slide 1)
        this.onLoad = () => {
          const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
          idle(() => this.slides.forEach((s) => this.undefer(s)));
        };
        if (document.readyState === 'complete') this.onLoad();
        else window.addEventListener('load', this.onLoad, { once: true });

        this.sync();
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        document.removeEventListener('visibilitychange', this.onVisibility);
        document.removeEventListener('shopify:block:select', this.onEditorSelect);
        document.removeEventListener('shopify:block:deselect', this.onEditorDeselect);
        window.removeEventListener('load', this.onLoad);
        if (this.motion.removeEventListener) this.motion.removeEventListener('change', this.onMotion);
        if (this.io) this.io.disconnect();
        this.initialized = false;
      }

      setHold(reason, on) {
        const had = this.holds.has(reason);
        if (on === had) return;
        if (on) this.holds.add(reason);
        else this.holds.delete(reason);
        this.sync();
      }

      sync() {
        const reduced = this.motion.matches;
        const playing = this.autoplay && !reduced && !this.userPaused;
        const running = playing && this.holds.size === 0;
        this.toggleAttribute('data-playing', playing);
        this.toggleAttribute('data-running', running);
        if (this.toggleBtn) {
          this.toggleBtn.hidden = !this.autoplay || reduced;
          this.toggleBtn.classList.toggle('is-paused', this.userPaused);
          this.toggleBtn.setAttribute('aria-label', this.userPaused ? LABEL_PLAY : LABEL_PAUSE);
        }
        // Während automatischer Wechsel nichts ansagen, sonst den neuen Slide höflich ansagen
        this.track.setAttribute('aria-live', playing ? 'off' : 'polite');
        if (running && this.pending) {
          this.pending = false;
          this.advance();
        }
      }

      // Bild-URLs weiterer Slides stehen bis hierhin nur in data-src/data-srcset (kein Laden vor dem load-Event)
      undefer(slide) {
        if (!slide.hasAttribute('data-defer')) return;
        slide.removeAttribute('data-defer');
        slide.querySelectorAll('source[data-srcset]').forEach((source) => {
          source.srcset = source.dataset.srcset;
          source.removeAttribute('data-srcset');
        });
        const img = slide.querySelector('img.gcs-hero__img');
        if (!img) return;
        img.loading = 'eager';
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        if (img.dataset.src) img.src = img.dataset.src;
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
      }

      whenReady(slide) {
        this.undefer(slide);
        const img = slide.querySelector('img.gcs-hero__img');
        if (!img || (img.complete && img.naturalWidth > 0)) return Promise.resolve();
        return new Promise((resolve) => {
          const done = () => {
            clearTimeout(timer);
            resolve();
          };
          const timer = setTimeout(done, 3000);
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
      }

      // Autoplay: nächsten Slide erst zeigen, wenn sein Bild da ist (sonst kurz eine leere Fläche)
      advance() {
        const next = (this.index + 1) % this.slides.length;
        const token = ++this.token;
        this.whenReady(this.slides[next]).then(() => {
          if (token !== this.token || !this.isConnected) return;
          if (this.hasAttribute('data-running')) this.go(next);
          else this.pending = true;
        });
      }

      go(target) {
        const count = this.slides.length;
        const next = ((target % count) + count) % count;
        this.token++;
        this.pending = false;
        if (next === this.index) {
          this.restart();
          return;
        }
        const prev = this.slides[this.index];
        const slide = this.slides[next];
        this.undefer(slide);
        prev.classList.remove('is-active');
        prev.setAttribute('aria-hidden', 'true');
        prev.setAttribute('inert', '');
        slide.classList.add('is-active');
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
        this.dots.forEach((dot, i) => {
          const current = i === next;
          dot.classList.toggle('is-current', current);
          if (current) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.index = next;
      }

      // Fortschritt des aktuellen Strichs neu starten (nach Berührung ohne Wischen)
      restart() {
        const fill = this.dots[this.index] && this.dots[this.index].querySelector('.gcs-hero__fill');
        if (!fill) return;
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
      }

      onDotKey(e, i) {
        const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        e.preventDefault();
        const count = this.dots.length;
        const next = (i + step + count) % count;
        this.go(next);
        this.dots[next].focus();
      }

      onDown(e) {
        if (e.pointerType === 'mouse' || !e.isPrimary) return;
        this.touch = { x: e.clientX, y: e.clientY, dx: 0, dy: 0 };
        this.setHold('touch', true);
      }

      onMove(e) {
        if (!this.touch || !e.isPrimary) return;
        this.touch.dx = e.clientX - this.touch.x;
        this.touch.dy = e.clientY - this.touch.y;
      }

      onUp(e, allowSwipe) {
        if (!this.touch || !e.isPrimary) return;
        const { dx, dy } = this.touch;
        this.touch = null;
        if (allowSwipe && Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
          this.go(this.index + (dx < 0 ? 1 : -1));
        } else {
          this.restart();
        }
        this.setHold('touch', false);
      }
    }
    customElements.define('gcs-hero-slider', GcsHeroSlider);
  }

  if (!customElements.get('gcs-carousel')) {
    class GcsCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-gcs-track]');
        this.nav = this.querySelector('[data-gcs-nav]');
        if (!this.track || !this.nav) return;
        this.prev = this.nav.querySelector('[data-gcs-prev]');
        this.next = this.nav.querySelector('[data-gcs-next]');
        this.nav.hidden = false;
        this.frame = 0;
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.update();
          });
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prev.addEventListener('click', () => this.go(-1));
        this.next.addEventListener('click', () => this.go(1));
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(this.onScroll);
          this.ro.observe(this.track);
        }
        this.update();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.ro) this.ro.disconnect();
      }

      step() {
        const item = this.track.firstElementChild;
        if (!item) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const itemW = item.getBoundingClientRect().width + gap;
        const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / itemW));
        return itemW * perView;
      }

      go(dir) {
        this.track.scrollBy({ left: dir * this.step(), behavior: reduceMotion() ? 'auto' : 'smooth' });
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const x = this.track.scrollLeft;
        const scrollable = max > 2;
        this.nav.classList.toggle('is-static', !scrollable);
        this.prev.disabled = !scrollable || x <= 2;
        this.next.disabled = !scrollable || x >= max - 2;
      }
    }
    customElements.define('gcs-carousel', GcsCarousel);
  }
})();
